/**
 * Ultra-Modern Modal Component
 * Professional Admin Portal Design
 */

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MdClose } from 'react-icons/md';
import { cn } from '../../lib/utils';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  size?: 'small' | 'compact' | 'medium' | 'large';
  /** Tighter header/body padding and smaller title */
  compact?: boolean;
  /** Disable body scroll (content should fit without overflow) */
  noScroll?: boolean;
}

export function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'medium',
  compact = false,
  noScroll = false,
}: ModalProps) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    small: 'max-w-md',
    compact: 'max-w-3xl',
    medium: 'max-w-2xl',
    large: 'max-w-4xl',
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-md"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={cn(
          'w-full bg-[hsl(var(--card))] rounded-xl shadow-2xl border border-[hsl(var(--border))] overflow-hidden flex flex-col',
          compact ? 'max-h-[92vh]' : 'max-h-[90vh]',
          sizeClasses[size]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={cn(
            'flex items-start justify-between gap-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--card))] shrink-0',
            compact ? 'border-t-2 border-t-[hsl(var(--primary))] px-5 py-3' : 'px-6 py-4'
          )}
        >
          <div className="min-w-0">
            <h2
              className={cn(
                'font-semibold text-[hsl(var(--foreground))]',
                compact ? 'text-base' : 'text-xl'
              )}
            >
              {title}
            </h2>
            {description ? (
              <p className="mt-0.5 text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className={cn(
              'rounded-lg hover:bg-[hsl(var(--muted))] transition-colors text-[hsl(var(--muted-foreground))] shrink-0',
              compact ? 'p-1.5' : 'p-2'
            )}
            aria-label="Close"
          >
            <MdClose className={compact ? 'w-4 h-4' : 'w-5 h-5'} />
          </button>
        </div>

        {/* Body */}
        <div
          className={cn(
            'flex-1 min-h-0',
            compact ? 'px-5 py-4' : 'p-6',
            noScroll ? 'overflow-hidden' : 'overflow-y-auto'
          )}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

